import { CurrencyPipe } from '@angular/common'
import { Component, DestroyRef, EventEmitter, Input, Output, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap'
import { CardComponent } from 'src/app/shared/components/card/card.component'
import { InputQuantityComponent } from 'src/app/shared/components/form-elements/input-quantity/input-quantity.component'
import { PartsArenaPartFinderComponent } from 'src/app/shared/components/partsarena/part-finder/parts-arena-part-finder.component'
import { SmartquoteBespokeProductSearchComponent } from 'src/app/shared/components/smartquote/smartquote-bespoke-product-search/smartquote-bespoke-product-search.component'
import { WolseleyProductSearchPulloutComponent } from 'src/app/shared/components/wolseley/wolseley-product-search-pullout/wolseley-product-search-pullout.component'
import { PAPartlistSectionRow } from 'src/app/shared/interfaces/partsarena/pa-partslistsectionrow.interface'
import { WolseleyProduct } from 'src/app/shared/interfaces/wolseley-product/wolseley-product.interface'
import { SmartquoteHelperService } from '../../../services/smartquote-helper.service'
import { InputMoneyComponent } from 'src/app/shared/components/form-elements/input-money/input-money.component'
import { ButtonComponent } from 'src/app/shared/components/form-elements/button/button.component'
import { InputComponent } from 'src/app/shared/components/form-elements/input/input.component'
import { SmartquoteFavouriteProductSearchPulloutComponent } from 'src/app/shared/components/smartquote/smartquote-favourite-product-search-pullout/smartquote-favourite-product-search-pullout.component'
import { SmartQuoteFavourite } from 'src/app/shared/interfaces/smartquote-favourite/smartquote-favourite.interface'
import { SmartQuoteLineForm } from '../../../helpers/smartquote-line-form.interface'
import { SmartQuoteBespokeFavourite } from 'src/app/shared/interfaces/smartquote-bespoke-favourite/smartquote-bespoke-favourite.interface'
import { SmartQuoteStore } from '../../../smartquote-edit-store.service'

@Component({
    selector: 'sf-smartquote-basket',
    imports: [
        ReactiveFormsModule,
        ButtonComponent,
        CardComponent,
        InputComponent,
        InputQuantityComponent,
        InputMoneyComponent,
        CurrencyPipe,
    ],
    templateUrl: './smartquote-basket.component.html',
    styleUrl: './smartquote-basket.component.scss',
})
export class SmartQuoteBasketComponent {
    private fb = inject(FormBuilder)
    private offcanvas = inject(NgbOffcanvas)
    private destroyRef = inject(DestroyRef)
    public smartquoteHelperService = inject(SmartquoteHelperService)

    @Input() form!: FormGroup
    @Input() gcNumber: string | null = null
    @Input() markup: number = 1

    @Output() lineAdded = new EventEmitter()
    @Output() changeDefaultImageUrl = new EventEmitter<string>()

    get lineItems(): FormArray<SmartQuoteLineForm> {
        return this.form.get('lineItems') as FormArray
    }

    removeLineItem(index: number) {
        const lineItemArray = this.form.get('lineItems') as FormArray
        lineItemArray.removeAt(index)
    }

    addLineItem(
        productId: number | null,
        catalogNumber: string | null,
        description: string,
        imageUrl: string,
        mpn: string,
        quantity: number,
        cost: number
    ): FormGroup {
        return this.fb.group({
            id: [0],
            productId: [productId],
            catalogNumber: [catalogNumber],
            description: [description],
            imageUrl: [imageUrl],
            mpn: [mpn],
            quantity: [quantity, [Validators.min(1)]],
            cost: [cost * this.markup, [Validators.required]],
            terms: [0],
        })
    }

    addItem(
        productId: number | null,
        catalogNumber: string | null,
        description: string,
        imageUrl: string,
        mpn: string,
        quantity: number,
        cost: number
    ): void {
        const exists = this.lineItems.value.some((line) => line.catalogNumber && line.catalogNumber === catalogNumber)

        if (exists) {
            const line = this.lineItems.controls.find((line) => line.value.catalogNumber === catalogNumber)
            line?.get('quantity')?.setValue(line.get('quantity')!.value + 1)
        } else {
            this.lineItems.push(this.addLineItem(productId, catalogNumber, description, imageUrl, mpn, quantity, cost))
        }
    }

    openPartsArenaSearch(): void {
        const ref = this.offcanvas.open(PartsArenaPartFinderComponent, { position: 'end' })

        ref.componentInstance.gcNumber = this.gcNumber
        ref.componentInstance.onPartSelected
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((part: PAPartlistSectionRow) => {
                this.addItem(null, part.catalogNumber, part.description, '', '', 1, part.trade)
                this.lineAdded.emit()
            })
    }

    openWolseleySearch(): void {
        const ref = this.offcanvas.open(WolseleyProductSearchPulloutComponent, { position: 'end' })
        ref.componentInstance.partSelected
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((part: WolseleyProduct) => {
                this.addItem(
                    part.id,
                    part.catalogNumber,
                    part.name,
                    part.imageUrl,
                    part.manufacturerPartNumber,
                    1,
                    part.price
                )
                this.lineAdded.emit()
            })
    }

    openSmartquoteBespokeFavouriteSearch(): void {
        const ref = this.offcanvas.open(SmartquoteBespokeProductSearchComponent, { position: 'end' })

        ref.componentInstance.partSelected
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((part: SmartQuoteBespokeFavourite) => {
                this.addItem(null, null, part.description, '', part.mpn, 1, part.cost)
            })
    }

    openSmartQuoteFavouriteSearch(): void {
        const ref = this.offcanvas.open(SmartquoteFavouriteProductSearchPulloutComponent, { position: 'end' })

        ref.componentInstance.partSelected
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((part: SmartQuoteFavourite) => {
                this.addItem(
                    part.productId,
                    part.catalogNumber,
                    part.name,
                    '',
                    part.manufacturerPartNumber,
                    1,
                    part.price || 0
                )
                this.lineAdded.emit()
            })
    }
}
